import { useDispatch } from "react-redux";
import { useCallback, useEffect } from "react";
import axios from "axios";
import { createCompany, resetCompany } from "../store/company";
import { handleLogin, onClickLogout, setInitialized, setTokenValid } from "../store/user";
import { getSetting, resetSetting } from "../store/scheduler";

const TOKEN_KEY = "token";
const LOGOUT_BROADCAST_KEY = "logout";
const COMPANY_CACHE_KEY = "company_cache";
const CACHE_DURATION = 5 * 60 * 1000;

// JWT를 파싱하여 payload를 반환
function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// JWT 만료 여부 사전 판별
function isTokenExpired(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return false;
  const nowMs = Date.now();
  const expMs = payload.exp * 1000;
  return nowMs >= expMs;
}

// 캐시 유틸리티 함수들
function getCachedData(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    if (now - timestamp > CACHE_DURATION) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function setCachedData(key, data) {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    console.error("캐시 저장 실패", error);
  }
}

export default function useUserAuthActions() {
  const dispatch = useDispatch();

  // Redux store와 캐시 초기화
  const resetAllData = useCallback(async () => {
    dispatch(onClickLogout());
    dispatch(resetSetting());
    dispatch(resetCompany());
    dispatch(setTokenValid(false));

    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (
          key.startsWith(COMPANY_CACHE_KEY) ||
          key === "monoti" ||
          key === "calendar" ||
          key === "cart"
        ) {
          localStorage.removeItem(key);
        }
      });

      const sessionKeys = [
        "monotiSessionData",
        "store",
        "roomId",
        "orderItems",
        "orderAddress",
        "paymentMethod",
      ];
      sessionKeys.forEach((key) => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          console.warn(`세션 스토리지 ${key} 삭제 실패`, e);
        }
      });
    } catch (error) {
      console.error("캐시 정리 실패", error);
    }
  }, [dispatch]);

  // 토큰 저장
  const saveToken = useCallback(async (token) => {
    localStorage.setItem(TOKEN_KEY, token);
  }, []);

  // 토큰 삭제
  const removeToken = useCallback(async () => {
    try {
      localStorage.removeItem(TOKEN_KEY);

      const logoutTime = Date.now().toString();
      localStorage.setItem(LOGOUT_BROADCAST_KEY, logoutTime);

      window.dispatchEvent(
        new StorageEvent("storage", {
          key: LOGOUT_BROADCAST_KEY,
          newValue: logoutTime,
          oldValue: null,
          storageArea: localStorage,
        }),
      );

      setTimeout(() => {
        localStorage.removeItem(LOGOUT_BROADCAST_KEY);
      }, 100);
    } catch (err) {
      console.error("토큰 삭제 실패", err);
    }
  }, []);

  // 로컬에 저장된 토큰 조회
  const getToken = useCallback(async () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (err) {
      console.error("토큰 조회 실패", err);
      return null;
    }
  }, []);

  // 토큰 검증 후 유저 정보 반환
  const getUserInfo = useCallback(async () => {
    const token = await getToken();

    if (!token) {
      dispatch(setTokenValid(false));
      await resetAllData();
      return null;
    }

    if (isTokenExpired(token)) {
      await removeToken();
      dispatch(setTokenValid(false));
      await resetAllData();
      return null;
    }

    const url = "/api/validate";
    const body = { token };

    try {
      const res = await axios.post(url, body);

      if (res.status === 200 && res.data) {
        dispatch(setTokenValid(true));
        return res.data;
      }

      dispatch(setTokenValid(false));
      await resetAllData();
      return null;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        await removeToken();
      }
      dispatch(setTokenValid(false));
      await resetAllData();
      return null;
    }
  }, [dispatch, getToken, removeToken, resetAllData]);

  // 회사 목록 조회
  const getCompanys = useCallback(
    async (mberNo) => {
      try {
        const cacheKey = `${COMPANY_CACHE_KEY}_${mberNo}`;
        const cachedCompanies = getCachedData(cacheKey);

        if (cachedCompanies) {
          dispatch(createCompany(cachedCompanies));
          return;
        }

        const url = "/api/mberCmpnys";
        const body = { mberNo };
        const res = await axios.post(url, body);

        if (res.status === 200 && Array.isArray(res.data)) {
          const companies = res.data.map((company) => ({
            ...company,
            gbn: "",
          }));

          dispatch(createCompany(companies));

          const storeChecks = companies.map(async (company) => {
            try {
              const isStore = await getStore(company.cmpnyNo);
              return { ...company, gbn: isStore ? "ss" : "" };
            } catch (error) {
              return company;
            }
          });

          const updatedCompanies = await Promise.all(storeChecks);

          setCachedData(cacheKey, updatedCompanies);
          dispatch(createCompany(updatedCompanies));
        }
      } catch (error) {
        console.error("회사 목록 조회 실패", error);
      }
    },
    [dispatch],
  );

  // 매장 여부 조회
  const getStore = useCallback(async (cmpnyNo) => {
    try {
      const url = "/erp/ss/company/get";
      const body = { cmpnyNo, isVisible: false };
      const res = await axios.post(url, body);
      return res?.data?.cmpnyNo || null;
    } catch {
      return null;
    }
  }, []);

  // 스케줄러 설정 조회
  const getSchedulerSetting = useCallback(
    async (mber) => {
      const url = "/api/toktokSettingList";
      const body = { mberNo: mber.mberNo };

      try {
        const res = await axios.post(url, body);
        if (res.status === 200) {
          dispatch(getSetting(res.data));
        }
      } catch (err) {
        console.error("스케줄 설정 조회 실패", err);
      }
    },
    [dispatch],
  );

  // 로그인 성공 처리
  const handleLoginSuccess = useCallback(
    async (member) => {
      await saveToken(member.accessToken);

      dispatch(handleLogin(member));

      await Promise.all([getCompanys(member.mberNo), getSchedulerSetting(member)]);
    },
    [dispatch, saveToken, getCompanys, getSchedulerSetting],
  );

  // 초기 로그인 체크
  const checkAndLogin = useCallback(async () => {
    const userInfo = await getUserInfo();
    dispatch(setInitialized(true));

    if (userInfo) {
      await handleLoginSuccess(userInfo);
      return true;
    } else {
      await resetAllData();
      return false;
    }
  }, [dispatch, getUserInfo, handleLoginSuccess, resetAllData]);

  // 로그아웃 처리
  const handleLogout = useCallback(async () => {
    try {
      await removeToken();
      await resetAllData();
    } catch (error) {
      console.error("로그아웃 처리 실패", error);
      try {
        await removeToken();
        await resetAllData();
      } catch (fallbackError) {
        console.error("로그아웃 fallback 처리 실패", fallbackError);
      }
    }
  }, [removeToken, resetAllData]);

  useEffect(() => {
    checkAndLogin();

    const handleStorageChange = async (event) => {
      switch (event.key) {
        case LOGOUT_BROADCAST_KEY: {
          await resetAllData();
          break;
        }
        case TOKEN_KEY: {
          const newToken = event.newValue;

          if (!newToken) {
            await resetAllData();
            break;
          }
          const userInfo = await getUserInfo();
          if (userInfo) {
            await handleLoginSuccess(userInfo);
          } else {
            await resetAllData();
          }
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [checkAndLogin, dispatch, getUserInfo, handleLoginSuccess]);

  return {
    handleLoginSuccess,
    checkAndLogin,
    removeToken,
    handleLogout,
  };
}
